import { NavLink } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { supabase } from '../lib/supabase';
import { DeskLogo } from './ui/DeskLogo';
import { OrbionCredit } from './ui/OrbionCredit';
import './Sidebar.css';

const NAV_SECTIONS = [
    {
        title: null,
        items: [
            { path: '/calendario', label: 'Calendário', icon: 'ph:calendar-blank-bold', roles: null },
        ],
    },
    {
        title: 'Eventos',
        items: [
            { path: '/nova-venda',         label: 'Nova Venda',            icon: 'ph:currency-circle-dollar-bold',  roles: ['closer', 'sales_farmer', 'gestor'] },
            { path: '/registro-eventos',   label: 'Registro de Eventos',   icon: 'ph:clock-counter-clockwise-bold', roles: null },
            { path: '/cadastro-leads',     label: 'Cadastro de Leads',     icon: 'ph:user-plus-bold',               roles: ['sdr', 'social_seller', 'closer', 'sales_farmer', 'gestor'] },
        ],
    },
    {
        title: 'Diários',
        items: [
            { path: '/diario-sdr',           label: 'Diário SDR',           icon: 'ph:clipboard-text-bold',   roles: ['sdr', 'gestor'] },
            { path: '/diario-social-seller', label: 'Diário Social Seller', icon: 'ph:chat-circle-text-bold', roles: ['social_seller', 'gestor'] },
            { path: '/diario-closer',        label: 'Diário Closer',        icon: 'ph:notebook-bold',          roles: ['closer', 'gestor'] },
            { path: '/diario-sales-farmer',  label: 'Diário Sales Farmer',  icon: 'ph:tree-bold',              roles: ['sales_farmer', 'gestor'] },
        ],
    },
    {
        title: 'Configurações',
        items: [
            { path: '/comissionamento', label: 'Comissionamento', icon: 'ph:chart-line-up-bold', roles: ['gestor'] },
            { path: '/configuracoes', label: 'Configurações', icon: 'ph:sliders-bold', roles: ['gestor'] },
        ],
    },
];

function initials(name) {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const Sidebar = ({ session, theme = 'light', onThemeToggle, role, currentUser, isOpen, onToggle }) => {
    const handleLogout = async () => {
        await supabase.auth.signOut();
    };

    const itemVisible = (item) => {
        if (!item.roles) return true;
        if (!role) return false;
        return item.roles.includes(role);
    };

    return (
        <aside className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
            <div className="sidebar-header">
                {isOpen ? (
                    <>
                        <div className="sidebar-logo">
                            <DeskLogo variant="full" size={28} theme={theme} />
                        </div>
                        <button
                            className="sidebar-toggle"
                            onClick={onToggle}
                            aria-label="Recolher menu"
                            title="Recolher"
                        >
                            <Icon icon="ph:caret-double-left-bold" width={14} height={14} />
                        </button>
                    </>
                ) : (
                    <>
                        <DeskLogo variant="symbol" size={36} theme={theme} />
                        <button
                            className="sidebar-toggle"
                            onClick={onToggle}
                            aria-label="Expandir menu"
                            title="Expandir"
                            style={{ marginTop: '0.25rem' }}
                        >
                            <Icon icon="ph:caret-double-right-bold" width={14} height={14} />
                        </button>
                    </>
                )}
            </div>

            <nav className="sidebar-nav">
                {NAV_SECTIONS.map((section, i) => {
                    const visibleItems = section.items.filter(itemVisible);
                    if (visibleItems.length === 0) return null;
                    return (
                        <div key={section.title ?? i} className="nav-section">
                            {isOpen && section.title && (
                                <div className="nav-section-title">{section.title}</div>
                            )}
                            {visibleItems.map((item) => (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    end={item.path === '/'}
                                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                                >
                                    {({ isActive }) => (
                                        <>
                                            <span className="nav-icon">
                                                <Icon icon={item.icon} width={17} height={17} />
                                            </span>
                                            {isOpen && <span className="nav-label">{item.label}</span>}
                                            {isOpen && isActive && (
                                                <span className="status-dot active nav-active-dot" aria-hidden="true" />
                                            )}
                                            <span className="nav-tooltip">{item.label}</span>
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </div>
                    );
                })}

                {isOpen && (
                    <div className="sidebar-orbion-credit">
                        <OrbionCredit size="sm" />
                    </div>
                )}
            </nav>

            <div className="sidebar-footer">
                {isOpen && session?.user?.email && (
                    <div className="sidebar-user">
                        <div className="sidebar-user-avatar">
                            {initials(currentUser?.nome || session.user.email.split('@')[0])}
                        </div>
                        <div className="sidebar-user-meta">
                            <div className="sidebar-user-name">{currentUser?.nome || session.user.email.split('@')[0]}</div>
                            <div className="sidebar-user-role">{currentUser?.cargo || '—'}</div>
                        </div>
                    </div>
                )}

                {!isOpen && session?.user?.email && (
                    <div className="sidebar-avatar-btn" title={currentUser?.nome || session.user.email}>
                        {initials(currentUser?.nome || session.user.email.split('@')[0])}
                        <span className="nav-tooltip">{currentUser?.nome || session.user.email}</span>
                    </div>
                )}

                {onThemeToggle && (
                    <button
                        className="nav-item"
                        onClick={onThemeToggle}
                        style={{ color: 'var(--dim)', background: 'none', border: '1px solid transparent' }}
                    >
                        <span className="nav-icon">
                            <Icon icon={theme === 'dark' ? 'ph:sun-bold' : 'ph:moon-bold'} width={17} height={17} />
                        </span>
                        {isOpen && (
                            <span className="nav-label">
                                {theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
                            </span>
                        )}
                        <span className="nav-tooltip">
                            {theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
                        </span>
                    </button>
                )}

                <button className="nav-item logout-btn" onClick={handleLogout}>
                    <span className="nav-icon"><Icon icon="ph:sign-out-bold" width={17} height={17} /></span>
                    {isOpen && <span className="nav-label">Sair</span>}
                    <span className="nav-tooltip">Sair</span>
                </button>
            </div>
        </aside>
    );
};

export default Sidebar;
